import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiList, FiLock, FiTrash2, FiUnlock } from "react-icons/fi";

const MIN_ZOOM = 1;
const MIN_VIEWPORT_DURATION_SECONDS = 0.01;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const formatTimeMs = (value) => {
  const safe = Math.max(0, Number(value) || 0);
  return safe.toFixed(3);
};

const getMaxZoomForDuration = (duration) =>
  Math.max(24, Math.ceil((Number(duration) || 0) / MIN_VIEWPORT_DURATION_SECONDS));

const resolveTrackUrl = (track, assets) => {
  if (!track) return null;
  if (track.url) return track.url;
  const audioAssets = assets?.audio ?? assets?.assets?.audio ?? [];
  const assetId =
    track.assetId ?? track.audioAssetId ?? track.id ?? track.audioId;
  if (!assetId) return null;
  const asset = audioAssets.find(
    (item) =>
      item.assetId === assetId ||
      item.id === assetId ||
      item.audioAssetId === assetId,
  );
  return asset?.url ?? null;
};

const getTrackStart = (track) =>
  Number(track?.start ?? track?.startTime ?? 0);

const getTrackVolume = (track) =>
  Number(track?.volume ?? track?.gain ?? 1);

const parseAudacityLabels = (text) =>
  String(text ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const parts = line.split("\t");
      if (parts.length < 2) return null;
      const start = Number.parseFloat(parts[0]);
      if (!Number.isFinite(start)) return null;
      const rawLabel =
        parts.slice(2).join("\t").trim() ||
        parts[1]?.trim() ||
        `M${index + 1}`;
      return {
        markerId: `marker_import_${Date.now()}_${index}`,
        time: Math.max(0, start),
        label: rawLabel,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);

const extractSpeechEntries = (scriptText) => {
  const entries = [];
  const text = String(scriptText ?? "");
  const callRegex = /\bspeak2?\s*\(/g;
  let match;

  while ((match = callRegex.exec(text)) !== null) {
    let index = callRegex.lastIndex;
    while (index < text.length && /\s/.test(text[index])) index += 1;
    const quote = text[index];
    if (!['"', "'", "`"].includes(quote)) continue;
    index += 1;
    let value = "";
    let escaped = false;
    while (index < text.length) {
      const ch = text[index];
      if (escaped) {
        value += ch;
        escaped = false;
      } else if (ch === "\\") {
        escaped = true;
      } else if (ch === quote) {
        break;
      } else {
        value += ch;
      }
      index += 1;
    }
    if (!value.trim()) continue;
    entries.push({
      text: value.trim(),
      method: match[0].startsWith("speak2") ? "speak2" : "speak",
    });
  }

  return entries;
};

const buildAutoMarkersFromScriptAndTracks = ({
  scriptText,
  decodedTracks,
}) => {
  const speechEntries = extractSpeechEntries(scriptText);
  const trackEntries = [...(decodedTracks || [])]
    .filter((track) => Number.isFinite(track.trackStart))
    .sort((a, b) => a.trackStart - b.trackStart);

  if (!speechEntries.length || !trackEntries.length) return [];

  return trackEntries.flatMap((trackEntry, index) => {
    const speechEntry = speechEntries[index];
    const labelSeed = speechEntry?.text || `Audio ${index + 1}`;
    const shortLabel =
      labelSeed.length > 36 ? `${labelSeed.slice(0, 36).trim()}...` : labelSeed;
    const startTime = Math.max(0, Number(trackEntry.trackStart) || 0);
    const endTime = Math.max(startTime, startTime + (Number(trackEntry.trackDuration) || 0));
    return [
      {
        markerId: `auto_${index + 1}_start`,
        time: startTime,
        label: `${shortLabel} Start`,
      },
      {
        markerId: `auto_${index + 1}_end`,
        time: endTime,
        label: `${shortLabel} End`,
      },
    ];
  });
};

const MAX_SAMPLES_PER_BUCKET = 96;

const collectRangeExtrema = (
  primaryChannel,
  secondaryChannel,
  startIdx,
  endIdx,
) => {
  const safeStart = Math.max(0, Math.min(primaryChannel.length - 1, startIdx));
  const safeEnd = Math.max(
    safeStart + 1,
    Math.min(primaryChannel.length, endIdx),
  );
  const span = safeEnd - safeStart;
  const stride = Math.max(1, Math.floor(span / MAX_SAMPLES_PER_BUCKET));
  let min = 1;
  let max = -1;

  for (let idx = safeStart; idx < safeEnd; idx += stride) {
    const sample = secondaryChannel
      ? (primaryChannel[idx] + secondaryChannel[idx]) * 0.5
      : primaryChannel[idx];
    if (sample < min) min = sample;
    if (sample > max) max = sample;
  }

  const lastIdx = safeEnd - 1;
  if (lastIdx >= safeStart) {
    const sample = secondaryChannel
      ? (primaryChannel[lastIdx] + secondaryChannel[lastIdx]) * 0.5
      : primaryChannel[lastIdx];
    if (sample < min) min = sample;
    if (sample > max) max = sample;
  }

  return { min, max };
};

const loadDecodedTracks = async ({
  tracks,
  assets,
  signal,
}) => {
  if (!tracks.length) return [];
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  try {
    const trackInfos = tracks
      .map((track) => ({
        track,
        url: resolveTrackUrl(track, assets),
      }))
      .filter((info) => Boolean(info.url));

    if (!trackInfos.length) return null;

    const decoded = [];
    for (const info of trackInfos) {
      const res = await fetch(info.url, { signal });
      if (!res.ok) continue;
      const arrayBuffer = await res.arrayBuffer();
      const buffer = await audioCtx.decodeAudioData(arrayBuffer);
      decoded.push({
        track: info.track,
        url: info.url,
        buffer,
        ch0: buffer.getChannelData(0),
        ch1:
          buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : null,
        sampleRate: buffer.sampleRate,
        trackStart: getTrackStart(info.track),
        trackDuration: buffer.duration || 0,
        trackVolume: Math.max(0, getTrackVolume(info.track)),
        trackLoop: Boolean(info.track?.loop),
      });
    }
    return decoded;
  } finally {
    audioCtx.close().catch(() => {});
  }
};

const MarkerOverlay = memo(function MarkerOverlay({
  markers,
  viewportStart,
  viewportDuration,
  selectedMarkerId,
  lockedMarkerId,
  segmentStartMarkerId,
  segmentEndMarkerId,
  onBeginMarkerDrag,
  onSelectMarker,
}) {
  return markers.map((marker) => {
    const left = ((marker.time - viewportStart) / viewportDuration) * 100;
    return (
      <div
        key={marker.markerId}
        className="editor-waveform-marker"
        style={{ left: `${left}%` }}
      >
        <button
          type="button"
          className={`editor-waveform-marker-label${
            selectedMarkerId === marker.markerId ? " active" : ""
          }${lockedMarkerId === marker.markerId ? " locked" : ""}${
            segmentStartMarkerId === marker.markerId ? " segment-start" : ""
          }${segmentEndMarkerId === marker.markerId ? " segment-end" : ""}`}
          onMouseDown={(event) => {
            event.stopPropagation();
            if (event.button !== 0) return;
            onBeginMarkerDrag(marker.markerId, event.clientX);
          }}
          onClick={(event) => {
            event.stopPropagation();
            onSelectMarker(
              selectedMarkerId === marker.markerId ? null : marker.markerId,
              true,
            );
          }}
        >
          {segmentStartMarkerId === marker.markerId ? "[" : ""}
          {lockedMarkerId === marker.markerId ? <FiLock size={10} /> : null}
          {marker.label}
          {segmentEndMarkerId === marker.markerId ? "]" : ""}
        </button>
      </div>
    );
  });
});

const MarkerList = memo(function MarkerList({
  showMarkerList,
  sortedMarkers,
  selectedMarkerId,
  lockedMarkerId,
  onSelectMarker,
  onPlayFromMarker,
  onInsertMarkerTime,
  onSetSegmentBoundary,
  onToggleMarkerLock,
  onDeleteMarker,
}) {
  if (!showMarkerList || sortedMarkers.length === 0) return null;

  return (
    <div className="editor-waveform-marker-list" role="list">
      {sortedMarkers.map((marker, index) => {
        const isActive = marker.markerId === selectedMarkerId;
        const isLocked = marker.markerId === lockedMarkerId;
        return (
          <div
            key={marker.markerId}
            role="listitem"
            className={`editor-waveform-marker-row${isActive ? " active" : ""}`}
          >
            <button
              type="button"
              className="editor-waveform-marker-row-main"
              onClick={() => {
                onSelectMarker(marker.markerId, true);
              }}
            >
              <span className="editor-waveform-marker-row-index">
                {index + 1}
              </span>
              <span className="editor-waveform-marker-row-label">
                {marker.label}
              </span>
              <span className="editor-waveform-marker-row-time">
                {formatTimeMs(marker.time)}
              </span>
            </button>
            <div className="editor-waveform-marker-row-actions">
              <button
                type="button"
                className="editor-waveform-marker-row-action"
                onClick={() => onPlayFromMarker(marker.markerId)}
              >
                Play
              </button>
              <button
                type="button"
                className="editor-waveform-marker-row-action"
                onClick={() => onInsertMarkerTime(marker.markerId)}
              >
                Insert
              </button>
              <button
                type="button"
                className="editor-waveform-marker-row-action"
                onClick={() => onSetSegmentBoundary("start", marker.markerId)}
              >
                Start
              </button>
              <button
                type="button"
                className="editor-waveform-marker-row-action"
                onClick={() => onSetSegmentBoundary("end", marker.markerId)}
              >
                End
              </button>
              <button
                type="button"
                className="editor-waveform-marker-row-action"
                onClick={() => onToggleMarkerLock(marker.markerId)}
              >
                {isLocked ? "Unlock" : "Lock"}
              </button>
              <button
                type="button"
                className="editor-waveform-marker-row-action danger"
                onClick={() => onDeleteMarker(marker.markerId)}
              >
                Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
});

const AudioWaveform = ({
  audioTracks = [],
  assets,
  currentScriptText = "",
  duration = 0,
  currentTime = 0,
  isPlaying = false,
  markers = [],
  selectedMarkerId = null,
  lockedMarkerId = null,
  onAddMarker = () => {},
  onUpdateMarker = () => {},
  onDeleteMarker = () => {},
  onToggleMarkerLock = () => {},
  onReplaceMarkers = () => {},
  onInsertMarkerTime = () => false,
  onPlayFromMarker = () => false,
  onBeginMarkerDrag = () => {},
  onSelectMarker = () => {},
  onSetSegmentBoundary = () => {},
  onPlaySegmentPreview = () => false,
  onClearSegmentPreview = () => {},
  segmentStartMarkerId = null,
  segmentEndMarkerId = null,
  canUndo = false,
  canRedo = false,
  onUndo = () => false,
  onRedo = () => false,
  onSeek = () => {},
}) => {
  const canvasRef = useRef(null);
  const shellRef = useRef(null);
  const scrollbarRef = useRef(null);
  const importInputRef = useRef(null);
  const playbackContextRef = useRef(null);
  const playbackSourcesRef = useRef([]);
  const playbackGainRef = useRef(null);
  const playbackRafRef = useRef(null);
  const playbackSegmentEndRef = useRef(null);
  const playbackStateRef = useRef({
    isPlaying: false,
    startedAtContextTime: 0,
    startOffset: 0,
  });
  const draggingRef = useRef(false);
  const scrollbarDragRef = useRef(false);
  const scrollbarStartRef = useRef(0);
  const scrollbarXRef = useRef(0);
  const [decodedTracks, setDecodedTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [viewportStart, setViewportStart] = useState(0);
  const [hoverTime, setHoverTime] = useState(null);
  const [layoutTick, setLayoutTick] = useState(0);
  const [activeMarkerTimeInput, setActiveMarkerTimeInput] = useState("");
  const [showMarkerList, setShowMarkerList] = useState(true);
  const [importError, setImportError] = useState("");
  const [waveformCurrentTime, setWaveformCurrentTime] = useState(currentTime);
  const [waveformIsPlaying, setWaveformIsPlaying] = useState(false);
  const userPannedRef = useRef(false);
  const clickRef = useRef({ x: 0, moved: false });
  const markerDragRef = useRef(null);
  const suppressMarkerClickRef = useRef(false);
  const maxZoom = useMemo(
    () => getMaxZoomForDuration(duration),
    [duration],
  );
  const displayCurrentTime = waveformIsPlaying ? waveformCurrentTime : currentTime;
  const displayIsPlaying = waveformIsPlaying || isPlaying;
  const hasLocalWaveformAudio = decodedTracks.length > 0;

  const viewportDuration = useMemo(
    () => (duration ? duration / zoom : 0),
    [duration, zoom],
  );

  const clampViewportStart = useCallback(
    (value, nextZoom = zoom) => {
      if (!duration) return 0;
      const nextViewport = duration / nextZoom;
      return clamp(value, 0, Math.max(0, duration - nextViewport));
    },
    [duration, zoom],
  );

  useEffect(() => {
    if (!duration) {
      setZoom(1);
      setViewportStart(0);
      return;
    }
    setViewportStart((prev) => clampViewportStart(prev, zoom));
  }, [duration, clampViewportStart, zoom]);

  useEffect(() => {
    if (!duration || audioTracks.length === 0) {
      setDecodedTracks([]);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);

    loadDecodedTracks({
      tracks: audioTracks,
      assets,
      signal: controller.signal,
    })
      .then((data) => {
        if (cancelled) return;
        setDecodedTracks(data);
      })
      .catch(() => {
        if (cancelled) return;
        setDecodedTracks([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [audioTracks, assets, duration]);

  const stopWaveformPlaybackLoop = useCallback(() => {
    if (playbackRafRef.current) {
      cancelAnimationFrame(playbackRafRef.current);
      playbackRafRef.current = null;
    }
  }, []);

  const stopWaveformSource = useCallback(() => {
    playbackSourcesRef.current.forEach((sourceNode) => {
      try {
        sourceNode.stop();
      } catch {}
      try {
        sourceNode.disconnect();
      } catch {}
    });
    playbackSourcesRef.current = [];
    playbackStateRef.current.isPlaying = false;
  }, []);

  const ensurePlaybackContext = useCallback(async () => {
    let audioContext = playbackContextRef.current;
    if (!audioContext) {
      audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      playbackContextRef.current = audioContext;
      playbackGainRef.current = audioContext.createGain();
      playbackGainRef.current.connect(audioContext.destination);
    }
    if (audioContext.state === "suspended") {
      await audioContext.resume().catch(() => {});
    }
    return audioContext;
  }, []);

  const getWaveformPlaybackTime = useCallback(() => {
    const context = playbackContextRef.current;
    const { isPlaying, startedAtContextTime, startOffset } = playbackStateRef.current;
    if (!context || !isPlaying) {
      return startOffset;
    }
    const elapsed = Math.max(0, context.currentTime - startedAtContextTime);
    return startOffset + elapsed;
  }, []);

  const pauseWaveformAudio = useCallback(
    (commitToParent = true) => {
      const nextTime = Math.max(
        Math.min(
          duration,
          getWaveformPlaybackTime(),
        ),
      );
      stopWaveformPlaybackLoop();
      stopWaveformSource();
      setWaveformIsPlaying(false);
      playbackStateRef.current.startOffset = Math.max(0, nextTime);
      setWaveformCurrentTime(nextTime);
      if (commitToParent) {
        onSeek(nextTime);
      }
    },
    [
      duration,
      getWaveformPlaybackTime,
      onSeek,
      stopWaveformPlaybackLoop,
      stopWaveformSource,
    ],
  );

  const seekWaveformAudio = useCallback(
    (time, options = {}) => {
      const { commitToParent = true } = options;
      const safeTime = Math.max(0, Math.min(duration, Number(time) || 0));
      setWaveformCurrentTime(safeTime);
      playbackStateRef.current.startOffset = safeTime;
      if (playbackStateRef.current.isPlaying) {
        stopWaveformSource();
        stopWaveformPlaybackLoop();
        setWaveformIsPlaying(false);
      }
      if (commitToParent) {
        onSeek(safeTime);
      }
      return safeTime;
    },
    [
      duration,
      onSeek,
      stopWaveformPlaybackLoop,
      stopWaveformSource,
    ],
  );

  const playWaveformAudio = useCallback(
    async (startTime = displayCurrentTime, endTime = null) => {
      if (!decodedTracks.length) return false;
      const safeTime = seekWaveformAudio(startTime, { commitToParent: true });
      playbackSegmentEndRef.current = endTime;
      stopWaveformPlaybackLoop();
      stopWaveformSource();
      const audioContext = await ensurePlaybackContext();
      const gainNode = playbackGainRef.current;
      if (!gainNode) return false;
      playbackStateRef.current = {
        isPlaying: true,
        startedAtContextTime: audioContext.currentTime,
        startOffset: safeTime,
      };

      const createdSources = [];
      decodedTracks.forEach((decodedTrack) => {
        const {
          buffer,
          trackStart,
          trackDuration,
          trackVolume,
          trackLoop,
        } = decodedTrack;
        const sourceNode = audioContext.createBufferSource();
        sourceNode.buffer = buffer;
        sourceNode.loop = trackLoop;
        const trackGain = audioContext.createGain();
        trackGain.gain.value = trackVolume;
        sourceNode.connect(trackGain);
        trackGain.connect(gainNode);

        const localStart = safeTime - trackStart;
        if (trackLoop && trackDuration > 0) {
          const offset =
            ((localStart % trackDuration) + trackDuration) % trackDuration;
          const when = audioContext.currentTime + Math.max(0, trackStart - safeTime);
          sourceNode.start(when, offset);
          createdSources.push(sourceNode);
          return;
        }

        if (safeTime <= trackStart) {
          sourceNode.start(audioContext.currentTime + (trackStart - safeTime), 0);
          createdSources.push(sourceNode);
          return;
        }

        if (safeTime < trackStart + trackDuration) {
          sourceNode.start(0, Math.max(0, localStart));
          createdSources.push(sourceNode);
        }
        });
      playbackSourcesRef.current = createdSources;

      const run = () => {
        const nextTime = getWaveformPlaybackTime();
        setWaveformCurrentTime(nextTime);
        if (
          playbackSegmentEndRef.current != null &&
          nextTime >= playbackSegmentEndRef.current
        ) {
          pauseWaveformAudio(true);
          seekWaveformAudio(playbackSegmentEndRef.current, {
            commitToParent: true,
          });
          playbackSegmentEndRef.current = null;
          return;
        }
        playbackRafRef.current = requestAnimationFrame(run);
      };

      setWaveformIsPlaying(true);
      setWaveformCurrentTime(safeTime);
      playbackRafRef.current = requestAnimationFrame(run);
      return true;
    },
    [
      decodedTracks,
      displayCurrentTime,
      ensurePlaybackContext,
      getWaveformPlaybackTime,
      pauseWaveformAudio,
      seekWaveformAudio,
      stopWaveformPlaybackLoop,
      stopWaveformSource,
    ],
  );

  useEffect(() => {
    if (!decodedTracks.length) {
      stopWaveformPlaybackLoop();
      stopWaveformSource();
      setWaveformIsPlaying(false);
      setWaveformCurrentTime(currentTime);
      return;
    }

    if (waveformIsPlaying) {
      return undefined;
    }

    seekWaveformAudio(currentTime, { commitToParent: false });

    return () => {
      stopWaveformPlaybackLoop();
      stopWaveformSource();
    };
  }, [
    currentTime,
    decodedTracks,
    waveformIsPlaying,
    seekWaveformAudio,
    stopWaveformPlaybackLoop,
    stopWaveformSource,
  ]);

  useEffect(() => {
    if (!waveformIsPlaying) {
      setWaveformCurrentTime(currentTime);
    }
  }, [currentTime, waveformIsPlaying]);

  useEffect(() => {
    return () => {
      stopWaveformPlaybackLoop();
      stopWaveformSource();
      const context = playbackContextRef.current;
      if (context && context.state !== "closed") {
        context.close().catch(() => {});
      }
      playbackContextRef.current = null;
      playbackGainRef.current = null;
    };
  }, [stopWaveformPlaybackLoop, stopWaveformSource]);

  useEffect(() => {
    if (!shellRef.current) return;
    const observer = new ResizeObserver(() => setLayoutTick((n) => n + 1));
    observer.observe(shellRef.current);
    return () => observer.disconnect();
  }, []);

  const updateZoom = useCallback(
    (nextZoom) => {
      setZoom((prev) => {
        const next = clamp(nextZoom, MIN_ZOOM, maxZoom);
        if (next === prev) return prev;
        setViewportStart((current) => {
          const relative =
            viewportDuration > 0
              ? (displayCurrentTime - current) / viewportDuration
              : 0.5;
          const nextViewport = duration / next;
          const nextStart = displayCurrentTime - relative * nextViewport;
          return clampViewportStart(nextStart, next);
        });
        return next;
      });
    },
    [clampViewportStart, displayCurrentTime, duration, maxZoom, viewportDuration],
  );

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const handleNativeWheel = (event) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      const direction = event.deltaY > 0 ? 1 : -1;
      const factor = direction > 0 ? 1 / 1.15 : 1.15;
      updateZoom(zoom * factor);
    };
    el.addEventListener("wheel", handleNativeWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleNativeWheel);
  }, [updateZoom, zoom]);

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const handleHorizontalScroll = (event) => {
      if (event.ctrlKey || !duration || zoom <= 1) return;
      event.preventDefault();
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;
      const ratio = delta / Math.max(120, Math.abs(delta));
      const step = viewportDuration * 0.18;
      userPannedRef.current = true;
      setViewportStart((prev) =>
        clampViewportStart(prev + ratio * step, zoom),
      );
    };
    el.addEventListener("wheel", handleHorizontalScroll, { passive: false });
    return () => el.removeEventListener("wheel", handleHorizontalScroll);
  }, [clampViewportStart, duration, viewportDuration, zoom]);

  useEffect(() => {
    if (isPlaying) {
      userPannedRef.current = false;
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!duration) return;
    const canvas = canvasRef.current;
    const host = shellRef.current;
    if (!canvas || !host) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = host.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);
    const midY = height / 2;

    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    ctx.fillStyle = "rgba(255, 255, 255, 0.02)";
    ctx.fillRect(0, 0, width, height);

    const usable = height * 0.45;
    ctx.strokeStyle = "rgba(123, 127, 138, 0.85)";
    ctx.lineWidth = 1;

    for (let x = 0; x < width; x += 1) {
      const windowStart = viewportStart + (x / width) * viewportDuration;
      const windowEnd =
        viewportStart + ((x + 1) / width) * viewportDuration;
      let minAmp = 0;
      let maxAmp = 0;
      let hasColumnData = false;

      decodedTracks.forEach((decodedTrack) => {
        const {
          ch0,
          ch1,
          sampleRate,
          trackStart,
          trackDuration,
          trackVolume,
          trackLoop,
        } = decodedTrack;
        let rangeMin = 0;
        let rangeMax = 0;
        let hasRange = false;

        if (trackLoop && trackDuration > 0) {
          const localStart = windowStart - trackStart;
          const localEnd = windowEnd - trackStart;
          if (localEnd <= 0) return;

          const normalizedStart =
            ((localStart % trackDuration) + trackDuration) % trackDuration;
          const normalizedEnd =
            ((localEnd % trackDuration) + trackDuration) % trackDuration;
          const coversFullLoop = windowEnd - windowStart >= trackDuration;

          if (coversFullLoop) {
            const extrema = collectRangeExtrema(
              ch0,
              ch1,
              0,
              Math.ceil(trackDuration * sampleRate),
            );
            rangeMin = extrema.min;
            rangeMax = extrema.max;
            hasRange = true;
          } else if (normalizedEnd >= normalizedStart) {
            const extrema = collectRangeExtrema(
              ch0,
              ch1,
              Math.floor(normalizedStart * sampleRate),
              Math.ceil(normalizedEnd * sampleRate),
            );
            rangeMin = extrema.min;
            rangeMax = extrema.max;
            hasRange = true;
          } else {
            const firstExtrema = collectRangeExtrema(
              ch0,
              ch1,
              Math.floor(normalizedStart * sampleRate),
              Math.ceil(trackDuration * sampleRate),
            );
            const secondExtrema = collectRangeExtrema(
              ch0,
              ch1,
              0,
              Math.ceil(normalizedEnd * sampleRate),
            );
            rangeMin = Math.min(firstExtrema.min, secondExtrema.min);
            rangeMax = Math.max(firstExtrema.max, secondExtrema.max);
            hasRange = true;
          }
        } else {
          if (
            windowEnd <= trackStart ||
            windowStart >= trackStart + trackDuration
          ) {
            return;
          }
          const sampleStart = Math.floor(
            Math.max(0, windowStart - trackStart) * sampleRate,
          );
          const sampleEnd = Math.ceil(
            Math.min(trackDuration, windowEnd - trackStart) * sampleRate,
          );
          const extrema = collectRangeExtrema(ch0, ch1, sampleStart, sampleEnd);
          rangeMin = extrema.min;
          rangeMax = extrema.max;
          hasRange = true;
        }

        if (!hasRange) return;
        minAmp = Math.min(minAmp, rangeMin * trackVolume);
        maxAmp = Math.max(maxAmp, rangeMax * trackVolume);
        hasColumnData = true;
      });

      if (!hasColumnData) continue;

      const topY = midY - maxAmp * usable;
      const bottomY = midY - minAmp * usable;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, topY);
      ctx.lineTo(x + 0.5, bottomY);
      ctx.stroke();
    }

    if (
      displayCurrentTime >= viewportStart &&
      displayCurrentTime <= viewportStart + viewportDuration
    ) {
      const playX =
        ((displayCurrentTime - viewportStart) / viewportDuration) * width;
      ctx.strokeStyle = "rgba(82, 182, 255, 0.9)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(playX + 0.5, 0);
      ctx.lineTo(playX + 0.5, height);
      ctx.stroke();
    }

    if (hoverTime != null) {
      const hoverX =
        ((hoverTime - viewportStart) / viewportDuration) * width;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(hoverX + 0.5, 0);
      ctx.lineTo(hoverX + 0.5, height);
      ctx.stroke();
    }
  }, [
    duration,
    decodedTracks,
    viewportStart,
    viewportDuration,
    displayCurrentTime,
    hoverTime,
    layoutTick,
  ]);

  useEffect(() => {
    if (!duration || zoom <= 1 || !displayIsPlaying) return;
    const end = viewportStart + viewportDuration;
    if (displayCurrentTime < viewportStart || displayCurrentTime > end) {
      setViewportStart(
        clampViewportStart(displayCurrentTime - viewportDuration * 0.5, zoom),
      );
    }
  }, [
    displayCurrentTime,
    displayIsPlaying,
    duration,
    zoom,
    viewportStart,
    viewportDuration,
    clampViewportStart,
  ]);

  const getTimeFromClientX = useCallback(
    (clientX) => {
      if (!duration || !shellRef.current) return null;
      const rect = shellRef.current.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / Math.max(rect.width, 1), 0, 1);
      return viewportStart + ratio * viewportDuration;
    },
    [duration, viewportStart, viewportDuration],
  );

  const handleSeekFromEvent = useCallback(
    (event) => {
      const time = getTimeFromClientX(event.clientX);
      if (time == null) return;
      if (hasLocalWaveformAudio) {
        seekWaveformAudio(time);
        return;
      }
      onSeek(time);
    },
    [getTimeFromClientX, hasLocalWaveformAudio, onSeek, seekWaveformAudio],
  );

  const handleMouseDown = useCallback(
    (event) => {
      if (event.target.closest(".editor-waveform-marker")) {
        draggingRef.current = false;
        clickRef.current = { x: event.clientX, moved: true };
        return;
      }
      onSelectMarker(null, false);
      draggingRef.current = true;
      clickRef.current = { x: event.clientX, moved: false };
      handleSeekFromEvent(event);
    },
    [handleSeekFromEvent],
  );

  useEffect(() => {
    const handleUp = () => {
      draggingRef.current = false;
      scrollbarDragRef.current = false;
    };
    window.addEventListener("mouseup", handleUp);
    return () => window.removeEventListener("mouseup", handleUp);
  }, []);

  const handleMouseMove = useCallback(
    (event) => {
      const time = getTimeFromClientX(event.clientX);
      if (time == null) return;
      if (markerDragRef.current?.markerId) {
        const moved =
          Math.abs(event.clientX - markerDragRef.current.startX) > 5;
        markerDragRef.current = {
          ...markerDragRef.current,
          moved: markerDragRef.current.moved || moved,
        };
        if (!markerDragRef.current.moved) {
          setHoverTime(time);
          return;
        }
        onUpdateMarker(markerDragRef.current.markerId, { time }, { skipHistory: true });
        setHoverTime(time);
        return;
      }
      if (draggingRef.current) {
        if (Math.abs(event.clientX - clickRef.current.x) > 4) {
          clickRef.current.moved = true;
        }
      }
      setHoverTime(time);
      if (draggingRef.current) {
        if (hasLocalWaveformAudio) {
          seekWaveformAudio(time);
        } else {
          onSeek(time);
        }
      }
    },
    [getTimeFromClientX, hasLocalWaveformAudio, onSeek, onUpdateMarker, seekWaveformAudio],
  );

  const handleMouseLeave = useCallback(() => {
    setHoverTime(null);
  }, []);

  const handleMouseUp = useCallback(
    (event) => {
      if (markerDragRef.current?.markerId) {
        suppressMarkerClickRef.current = Boolean(markerDragRef.current.moved);
        markerDragRef.current = null;
        return;
      }
      if (!duration || !shellRef.current) return;
      if (event.target.closest(".editor-waveform-marker")) return;
      if (!clickRef.current.moved) {
        const time = getTimeFromClientX(event.clientX);
        if (time == null) return;
        onAddMarker(time);
      }
    },
    [duration, getTimeFromClientX, onAddMarker],
  );

  const handleScrollbarMouseDown = useCallback(
    (event) => {
      if (!duration || zoom <= 1) return;
      event.stopPropagation();
      scrollbarDragRef.current = true;
      scrollbarStartRef.current = viewportStart;
      scrollbarXRef.current = event.clientX;
      event.preventDefault();
    },
    [duration, zoom, viewportStart],
  );

  const handleScrollbarTrackClick = useCallback(
    (event) => {
      if (!duration || zoom <= 1) return;
      const track = event.currentTarget;
      const rect = track.getBoundingClientRect();
      const ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const nextStart = ratio * (duration - viewportDuration);
      userPannedRef.current = true;
      setViewportStart(clampViewportStart(nextStart, zoom));
    },
    [duration, viewportDuration, clampViewportStart, zoom],
  );

  useEffect(() => {
    const handleMove = (event) => {
      if (!scrollbarDragRef.current) return;
      const track = scrollbarRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const dx = event.clientX - scrollbarXRef.current;
      const ratio = dx / Math.max(1, rect.width);
      const nextStart =
        scrollbarStartRef.current + ratio * (duration - viewportDuration);
      userPannedRef.current = true;
      setViewportStart(clampViewportStart(nextStart, zoom));
    };
    const handleUp = () => {
      scrollbarDragRef.current = false;
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [clampViewportStart, duration, viewportDuration, zoom]);

  const handleWheel = useCallback(
    (event) => {
      if (!event.ctrlKey || !duration) return;
      event.preventDefault();
      const direction = event.deltaY > 0 ? 1 : -1;
      const factor = direction > 0 ? 1 / 1.15 : 1.15;
      updateZoom(zoom * factor);
    },
    [duration, updateZoom, zoom],
  );

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setViewportStart(0);
  }, []);

  const handleZoomIn = useCallback(() => {
    updateZoom(zoom * 1.25);
  }, [updateZoom, zoom]);

  const handleZoomOut = useCallback(() => {
    updateZoom(zoom / 1.25);
  }, [updateZoom, zoom]);

  const hasAudio = audioTracks.length > 0;
  const activeMarker = useMemo(
    () => markers.find((marker) => marker.markerId === selectedMarkerId) || null,
    [markers, selectedMarkerId],
  );
  const sortedMarkers = useMemo(
    () => [...markers].sort((a, b) => a.time - b.time),
    [markers],
  );
  const visibleMarkers = useMemo(
    () =>
      markers.filter(
        (marker) =>
          marker.time >= viewportStart &&
          marker.time <= viewportStart + viewportDuration,
      ),
    [markers, viewportDuration, viewportStart],
  );

  useEffect(() => {
    if (!selectedMarkerId) return;
    if (!markers.some((marker) => marker.markerId === selectedMarkerId)) {
      onSelectMarker(null, false);
    }
  }, [markers, onSelectMarker, selectedMarkerId]);

  useEffect(() => {
    setActiveMarkerTimeInput(
      activeMarker ? formatTimeMs(activeMarker.time) : "",
    );
  }, [activeMarker]);

  const commitMarkerTime = useCallback(() => {
    if (!activeMarker) return;
    const parsed = Number(String(activeMarkerTimeInput).trim());
    if (!Number.isFinite(parsed)) {
      setActiveMarkerTimeInput(formatTimeMs(activeMarker.time));
      return;
    }
    const safeTime = clamp(parsed, 0, duration || parsed);
    onUpdateMarker(activeMarker.markerId, { time: safeTime });
    setActiveMarkerTimeInput(formatTimeMs(safeTime));
  }, [activeMarker, activeMarkerTimeInput, duration, onUpdateMarker]);

  const handleBeginMarkerDrag = useCallback(
    (markerId, clientX) => {
      suppressMarkerClickRef.current = false;
      onBeginMarkerDrag(markerId);
      markerDragRef.current = {
        markerId,
        startX: clientX,
        moved: false,
      };
    },
    [onBeginMarkerDrag],
  );

  const handleSelectMarkerClick = useCallback(
    (markerId, shouldSeek) => {
      if (suppressMarkerClickRef.current) {
        suppressMarkerClickRef.current = false;
        return;
      }
      if (hasLocalWaveformAudio && shouldSeek) {
        const marker = markers.find((item) => item.markerId === markerId);
        onSelectMarker(markerId, false);
        if (marker) {
          seekWaveformAudio(marker.time);
        }
        return;
      }
      onSelectMarker(markerId, shouldSeek);
    },
    [hasLocalWaveformAudio, markers, onSelectMarker, seekWaveformAudio],
  );

  const handleImportLabelsClick = useCallback(() => {
    setImportError("");
    importInputRef.current?.click();
  }, []);

  const handleClearMarkersClick = useCallback(() => {
    onReplaceMarkers([]);
    setImportError("");
  }, [onReplaceMarkers]);

  const handleAutoMarkClick = useCallback(() => {
    const generatedMarkers = buildAutoMarkersFromScriptAndTracks({
      scriptText: currentScriptText,
      decodedTracks,
    });
    if (!generatedMarkers.length) {
      setImportError("No speak()/speak2() lines or audio tracks found");
      return;
    }
    onReplaceMarkers(generatedMarkers);
    setImportError("");
  }, [currentScriptText, decodedTracks, onReplaceMarkers]);

  const handleImportLabelsChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const importedMarkers = parseAudacityLabels(text);
        if (!importedMarkers.length) {
          setImportError("No Audacity labels found");
          return;
        }
        onReplaceMarkers(importedMarkers);
        setImportError("");
      } catch {
        setImportError("Failed to read labels");
      } finally {
        event.target.value = "";
      }
    },
    [onReplaceMarkers],
  );

  const handlePlayMarker = useCallback(
    (markerId) => {
      const marker = markers.find((item) => item.markerId === markerId);
      if (!marker) return false;
      if (hasLocalWaveformAudio) {
        return playWaveformAudio(marker.time);
      }
      return onPlayFromMarker(markerId);
    },
    [hasLocalWaveformAudio, markers, onPlayFromMarker, playWaveformAudio],
  );

  const handlePlaySegment = useCallback(() => {
    if (!segmentStartMarkerId || !segmentEndMarkerId) return false;
    const startMarker = markers.find((item) => item.markerId === segmentStartMarkerId);
    const endMarker = markers.find((item) => item.markerId === segmentEndMarkerId);
    if (!startMarker || !endMarker) return false;
    const [startTime, endTime] =
      startMarker.time <= endMarker.time
        ? [startMarker.time, endMarker.time]
        : [endMarker.time, startMarker.time];
    if (hasLocalWaveformAudio) {
      return playWaveformAudio(startTime, endTime);
    }
    return onPlaySegmentPreview();
  }, [
    hasLocalWaveformAudio,
    markers,
    onPlaySegmentPreview,
    playWaveformAudio,
    segmentEndMarkerId,
    segmentStartMarkerId,
  ]);

  return (
    <div className="editor-waveform-section">
      <input
        ref={importInputRef}
        type="file"
        accept=".txt,.labels"
        className="editor-waveform-import-input"
        onChange={handleImportLabelsChange}
      />
        <div className="editor-waveform-header">
          <span className="editor-waveform-title">Audio Waveform</span>
          <span className="editor-waveform-hint">
            {hasAudio ? "Ctrl + Wheel to zoom" : "No audio tracks"}
          </span>
          <div className="editor-waveform-actions">
            <button
              type="button"
              className="editor-waveform-zoom-btn"
              onClick={handleZoomOut}
              aria-label="Zoom out waveform"
          >
            -
          </button>
          <span className="editor-waveform-zoom-label">
            {zoom.toFixed(2)}x
          </span>
          <button
            type="button"
            className="editor-waveform-zoom-btn"
            onClick={handleZoomIn}
            aria-label="Zoom in waveform"
          >
            +
          </button>
            {sortedMarkers.length > 0 && (
              <button
                type="button"
                className={`editor-waveform-zoom-btn${
                  showMarkerList ? " active" : ""
              }`}
              onClick={() => setShowMarkerList((current) => !current)}
              aria-label={showMarkerList ? "Hide marker list" : "Show marker list"}
              title={showMarkerList ? "Hide marker list" : "Show marker list"}
              >
                <FiList size={11} />
              </button>
            )}
            {sortedMarkers.length > 0 && (
              <button
                type="button"
                className="editor-waveform-zoom-btn"
                onClick={handleClearMarkersClick}
                aria-label="Clear all markers"
                title="Clear all markers"
              >
                <FiTrash2 size={10} />
              </button>
            )}
            {zoom > 1 && (
              <button
                type="button"
                className="editor-waveform-reset"
                onClick={handleResetZoom}
            >
                Reset
              </button>
            )}
            <button
              type="button"
              className="editor-waveform-reset"
              onClick={handleAutoMarkClick}
              title="Generate markers from speak() / speak2() lines"
            >
              Auto
            </button>
            <button
              type="button"
              className="editor-waveform-reset"
              onClick={handleImportLabelsClick}
            >
            Import
          </button>
        </div>
      </div>

      <div
        className="editor-waveform-shell"
        ref={shellRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        role="slider"
        aria-label="Audio waveform"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={displayCurrentTime}
      >
        {loading && <div className="editor-waveform-loading">Loading...</div>}
        {!loading && decodedTracks.length === 0 && (
          <div className="editor-waveform-empty">
            {hasAudio ? "Waveform unavailable" : "Add audio to preview it here"}
          </div>
        )}
        <canvas ref={canvasRef} className="editor-waveform-canvas" />
        <MarkerOverlay
          markers={visibleMarkers}
          viewportStart={viewportStart}
          viewportDuration={viewportDuration}
          selectedMarkerId={selectedMarkerId}
          lockedMarkerId={lockedMarkerId}
          segmentStartMarkerId={segmentStartMarkerId}
          segmentEndMarkerId={segmentEndMarkerId}
          onBeginMarkerDrag={handleBeginMarkerDrag}
          onSelectMarker={handleSelectMarkerClick}
        />
        {hoverTime != null && (
          <div className="editor-waveform-tooltip">
            {formatTimeMs(hoverTime)}
          </div>
        )}
      </div>

      <div
        className="editor-waveform-scrollbar"
        ref={scrollbarRef}
        onMouseDown={handleScrollbarTrackClick}
        role="presentation"
      >
        <div
          className="editor-waveform-scrollbar-handle"
          style={{
            width: `${duration ? Math.max((viewportDuration / duration) * 100, 6) : 100}%`,
            left: `${duration ? (viewportStart / duration) * 100 : 0}%`,
          }}
          onMouseDown={handleScrollbarMouseDown}
          role="presentation"
        />
      </div>

      <div className="editor-waveform-footer">
        <div className="editor-waveform-footer-times">
          <span className="editor-waveform-footer-time">
            {formatTimeMs(viewportStart)}
          </span>
          <span className="editor-waveform-footer-time">
            {formatTimeMs(viewportStart + viewportDuration)}
          </span>
          <span className="editor-waveform-footer-current">
            {displayIsPlaying ? "Playing" : "Paused"} - {formatTimeMs(displayCurrentTime)}
          </span>
          {importError ? (
            <span className="editor-waveform-import-error">{importError}</span>
          ) : null}
        </div>
        {activeMarker && (
          <div className="editor-waveform-marker-editor">
            <input
              type="text"
              className="editor-waveform-marker-input"
              value={activeMarker.label}
              maxLength={50}
              onChange={(event) =>
                onUpdateMarker(activeMarker.markerId, {
                  label: event.target.value,
                })
              }
              placeholder="Marker label"
            />
            <input
              type="text"
              inputMode="decimal"
              className="editor-waveform-marker-input editor-waveform-marker-time-input"
              value={activeMarkerTimeInput}
              onChange={(event) => setActiveMarkerTimeInput(event.target.value)}
              onBlur={commitMarkerTime}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  commitMarkerTime();
                }
              }}
              placeholder="0.000"
              aria-label="Marker time in seconds"
            />
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={() => handlePlayMarker(activeMarker.markerId)}
            >
              Play
            </button>
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={() => onSetSegmentBoundary("start", activeMarker.markerId)}
            >
              Set start
            </button>
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={() => onSetSegmentBoundary("end", activeMarker.markerId)}
            >
              Set end
            </button>
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={() => onInsertMarkerTime(activeMarker.markerId)}
            >
              Insert time
            </button>
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={() => onDeleteMarker(activeMarker.markerId)}
            >
              Delete
            </button>
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={() => onToggleMarkerLock(activeMarker.markerId)}
            >
              {lockedMarkerId === activeMarker.markerId ? (
                <>
                  <FiUnlock size={10} /> Unlock
                </>
              ) : (
                <>
                  <FiLock size={10} /> Lock
                </>
              )}
            </button>
            <button
              type="button"
              className="editor-waveform-marker-action subtle"
              onClick={() => onSelectMarker(null, false)}
            >
              Close
            </button>
          </div>
        )}
        {(segmentStartMarkerId || segmentEndMarkerId || canUndo || canRedo) && (
          <div className="editor-waveform-marker-editor">
            <span className="editor-waveform-marker-time">
              Segment
            </span>
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={handlePlaySegment}
              disabled={!segmentStartMarkerId || !segmentEndMarkerId}
            >
              Preview segment
            </button>
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={onClearSegmentPreview}
              disabled={!segmentStartMarkerId && !segmentEndMarkerId}
            >
              Clear
            </button>
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={onUndo}
              disabled={!canUndo}
            >
              Undo
            </button>
            <button
              type="button"
              className="editor-waveform-marker-action"
              onClick={onRedo}
              disabled={!canRedo}
            >
              Redo
            </button>
          </div>
        )}
        <MarkerList
          showMarkerList={showMarkerList}
          sortedMarkers={sortedMarkers}
          selectedMarkerId={selectedMarkerId}
          lockedMarkerId={lockedMarkerId}
          onSelectMarker={handleSelectMarkerClick}
          onPlayFromMarker={handlePlayMarker}
          onInsertMarkerTime={onInsertMarkerTime}
          onSetSegmentBoundary={onSetSegmentBoundary}
          onToggleMarkerLock={onToggleMarkerLock}
          onDeleteMarker={onDeleteMarker}
        />
      </div>
    </div>
  );
};

export default AudioWaveform;
